// ── Zip → service day ─────────────────────────────────────────────
// There is deliberately NO hardcoded zip→day table here. One used to exist
// (placeholder New York zips 10001-10023) and was copy-pasted into three
// separate "is this client in our service area?" checks — ClientProfile's
// technician banner, the Clients list "OUT OF AREA" chip, and send-invoice's
// service-day line. No real client ever matched it, so two of those checks
// were permanently true (every client flagged out-of-area) and the third
// silently printed "Saturday" on every invoice. Resolve from the operator's
// own service_area, and from the client when you have one.

// Module-private mutable mapping populated from the DB at runtime.
// Use setDynamicZipDays() to update; getDayForZip() reads it.
let dynamicZipDays = {};
export function setDynamicZipDays(next) { dynamicZipDays = next || {}; }

// Service day comes ONLY from the operator's saved service area — the passed
// map, else the DB-loaded global. No hardcoded zip→day fallback: an unmapped zip
// returns null ("No service day") so the operator stays in full control and the
// UI matches what the backend (generate-monthly-jobs / create-customer) actually
// uses to schedule.
export const getDayForZip = (zip, dynamicArea) => {
  if (!zip) return null;
  const source = (dynamicArea && Object.keys(dynamicArea).length > 0)
    ? dynamicArea
    : dynamicZipDays;
  for (const [day, zips] of Object.entries(source)) if (zips.includes(zip)) return day;
  return null;
};

// ── Which day is THIS client serviced on? ────────────────────────
// The zip mapping is the default so a neighbourhood rides one route, but
// `clients.service_day_override` (sql/060) is a per-client exception that wins
// outright — including for a client whose zip isn't mapped at all, which is the
// whole point of "change the service day regardless of the zip code".
//
// ALWAYS use this instead of getDayForZip() when you have the client object.
// This day is shown to the operator AND printed on payment links and invoices;
// resolving it two different ways is how a customer gets told Saturday while a
// tech is routed on Monday. Mirrors resolveServiceDays() in the edge functions.
export const getServiceDayForClient = (client, dynamicArea) => {
  if (!client) return null;
  return client.service_day_override || getDayForZip(client.zip, dynamicArea) || null;
};

// True when this client is deliberately off their zip's route.
export const hasDayOverride = (client) =>
  !!(client && (client.service_day_override || client.second_day_override));

// The SECOND service day, for twice-weekly clients only. Falls back to the zip's
// second day when only the primary was overridden, unless that would duplicate
// the primary (in which case there is no usable second day).
export const getSecondDayForClient = (client, zipSecondDay = {}) => {
  if (!client || client.frequency !== "twice-weekly") return null;
  const primary = getServiceDayForClient(client);
  const second  = client.second_day_override || zipSecondDay[client.zip] || null;
  return second && primary && second === primary ? null : second;
};

export const DAYS       = ["sunday","monday","tuesday","wednesday","thursday","friday","saturday"];
export const DAY_LABELS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
export const dayTitle   = (d) => (d ? d.charAt(0).toUpperCase() + d.slice(1) : null);

// ── Map constants (color/shape encoding for client markers) ──────
export const DAY_COLORS = {
  monday:    "#3B82F6",
  tuesday:   "#8B5CF6",
  wednesday: "#EC4899",
  thursday:  "#F97316",
  friday:    "#10B981",
  saturday:  "#072C76",
  sunday:    "#EF4444",
};
export const FREQ_SHAPES = {
  weekly:         "circle",
  biweekly:       "diamond",
  "twice-weekly": "star",
};
